/**
 * A once-per-`(kind, entityId)` ledger for share moments, in `localStorage`.
 *
 * <p>The moment is recorded the instant the dialog **opens**, not when it is dismissed. That ordering is
 * the point: a reload with the arming parameter still in the address bar must not re-show it, and
 * recording on dismissal would leave a window where a refresh re-prompts an author who already said no.
 *
 * Two independent guards cover the same failure, deliberately — this ledger, and the param-strip effect
 * on the page. Either alone would be enough on a good day; the combination is what makes "it showed up
 * twice" not a bug anyone has to reproduce.
 *
 * Storage is best-effort. Private-browsing modes and storage-quota errors make `localStorage` throw, and
 * a share prompt is not worth an exception: every failure degrades to "show the moment", which is the
 * pre-existing behavior rather than a new one.
 */

import type { ShareMomentKind } from "@/main/util/PlatformIdentifierUtil";

const STORAGE_KEY = "sv_share_moments";

/**
 * Keeps the ledger bounded. A prolific author publishing daily would otherwise grow this entry without
 * limit, and `localStorage` is a shared, small budget — an unbounded key here can start evicting other
 * features' state rather than its own.
 */
const MAX_ENTRIES = 200;

/**
 * The ledger keys one entry per (kind, entity), so it must accept exactly the kinds that can arm a
 * dialog — no more, no less.
 *
 * <p>DERIVED, not restated. This was a third hand-maintained copy of the same value list
 * (`ShareMomentKind`, `shareMomentParam.KNOWN_KINDS`, and this), and adding `first_post` to two of
 * three would have compiled while silently letting the new moment re-show on every visit, because a
 * kind the ledger does not know is a kind it cannot record. Deleting the copy is the fix; keeping it
 * in sync is not (rule 31's "delete a copy, never guard the sync", applied to a type).
 */
export type ShareMomentLedgerKind = ShareMomentKind;

function ledgerKey(kind: ShareMomentLedgerKind, entityId: string): string {
    return `${kind}:${entityId}`;
}

function readEntries(): string[] {
    try {
        if (typeof window === "undefined" || !window.localStorage) {
            return [];
        }
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === "string") : [];
    } catch {
        // Corrupt or unreadable ledger: treat as empty rather than throwing into a render path.
        return [];
    }
}

function writeEntries(entries: string[]): void {
    try {
        if (typeof window === "undefined" || !window.localStorage) {
            return;
        }
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
        // Quota or private-browsing failure. The moment has already been shown; losing the record only
        // risks showing it once more, which is strictly better than throwing.
    }
}

/** True when this exact moment has already been shown for this entity. */
export function hasShownShareMoment(kind: ShareMomentLedgerKind, entityId: string): boolean {
    if (!entityId) {
        return false;
    }
    return readEntries().includes(ledgerKey(kind, entityId));
}

/** Records that the moment was shown. Idempotent; evicts oldest-first past {@link MAX_ENTRIES}. */
export function recordShareMomentShown(kind: ShareMomentLedgerKind, entityId: string): void {
    if (!entityId) {
        return;
    }
    const key = ledgerKey(kind, entityId);
    const entries = readEntries().filter((entry) => entry !== key);
    entries.push(key);
    writeEntries(entries.slice(-MAX_ENTRIES));
}

/** Test-only reset. */
export function clearShareMomentLedger(): void {
    try {
        window?.localStorage?.removeItem(STORAGE_KEY);
    } catch {
        // ignore
    }
}
